import { useState, useCallback } from 'react';
import type { Context, ContextDefinition, ContextLevel } from 'types/context';
import type { TreeMapViewConfig } from 'types/contextView';
import type {
  ContextViewRecord,
  CreateContextPayload,
  CreateContextViewPayload,
} from '../../../api/contextApi';
import type { DirtyKind } from '../../molecules/ContextSwitcherToolbar/ContextSwitcherToolbarMolecule';

/**
 * `DirtyKind` is defined once, on `ContextSwitcherToolbarMolecule` (the
 * publicly barrelled home) — re-exported here so this hook's own consumers
 * don't need a second import path. It used to be declared independently in
 * both places and type-checked only by structural identity (EAINT-789 gap
 * analysis G-1); a divergence there would have compiled cleanly and failed
 * silently at runtime.
 */
export type { DirtyKind };

export interface SavePayload {
  contextUpdate: { definition: ContextDefinition } | null;
}

export interface CopyPayload {
  newContext: CreateContextPayload;
  newView: CreateContextViewPayload | null;
}

export interface UseContextDirtyStateReturn {
  isDirty: boolean;
  dirtyKinds: DirtyKind[];
  applyDefinitionPatch: (patch: DefinitionPatch) => void;
  reset: () => void;
  buildSavePayload: () => SavePayload;
  buildCopyPayload: (newName: string, newDescription?: string) => CopyPayload;
}

/** A partial ContextDefinition where levels are identified by id for targeted merging */
export type DefinitionPatch = {
  levels?: Array<Partial<Omit<ContextLevel, 'id'>> & { id: string }>;
  root?: Partial<ContextDefinition['root']>;
  settings?: Partial<ContextDefinition['settings']>;
};

/**
 * Deep-merges a definition patch into an existing ContextDefinition.
 * Levels are merged by id — only levels referenced in the patch are updated.
 */
function applyPatchToDefinition(
  base: ContextDefinition,
  patch: DefinitionPatch
): ContextDefinition {
  let result = { ...base };

  if (patch.levels?.length) {
    const merged = result.levels.map((level) => {
      const levelPatch = patch.levels!.find((p) => p.id === level.id);
      return levelPatch ? { ...level, ...levelPatch } : level;
    });
    result = { ...result, levels: merged };
  }

  if (patch.root) {
    result = { ...result, root: { ...result.root, ...patch.root } };
  }

  if (patch.settings) {
    result = { ...result, settings: { ...result.settings, ...patch.settings } };
  }

  return result;
}

/**
 * Tracks pending definition changes for a loaded context + view pair — the
 * shared, everyone-sees-it rail behind Save / Save-as-copy.
 *
 * One dirty bucket:
 *  - `filters` → changes to context.definition (levels, sort, root, settings)
 *
 * It used to carry two more (`viewConfig`, `baseConfig`), fed by
 * `applyViewConfigPatch` / `applyBaseConfigPatch`. Both were removed once
 * EAINT-780 (`5c431b72c`) routed board state — column widths, hidden columns,
 * collapsed nodes, density — into the per-user personalization overlay instead:
 * a personal arrangement must never become everyone's, so it deliberately does
 * not touch this rail. The view editor is the other writer of view/base config
 * and calls `updateContextView` directly. That left both setters with no
 * producer, and therefore left `SavePayload.viewUpdate` permanently `null` —
 * unreachable code that read as a working save path.
 *
 * `filters` joined them as of EAINT-781 Phase 6: `TreeMapOrganism` no longer
 * calls `applyDefinitionPatch` from its level-filter UI — composed
 * session/user/view filters go through `useLevelFilters.setLevelFilter`
 * instead, and relaxing a context-authored (locked) clause is routed to
 * "Edit in context" (a navigation to the context editor), per the spec's
 * Widening rule: a view/user filter can never relax what the context
 * authored. `applyDefinitionPatch` stays exported for the next real
 * producer of a structural definition change.
 *
 * Lives next to useTreeMap — TreeMap-shaped for now, extractable later.
 */
export function useContextDirtyState(
  loadedContext: Context | null,
  loadedView: ContextViewRecord | null
): UseContextDirtyStateReturn {
  const [pendingDefinition, setPendingDefinition] = useState<ContextDefinition | null>(null);

  const isDirty = pendingDefinition !== null;

  const dirtyKinds: DirtyKind[] = pendingDefinition ? ['filters'] : [];

  const applyDefinitionPatch = useCallback(
    (patch: DefinitionPatch) => {
      if (!loadedContext) return;
      setPendingDefinition((prev) => {
        const base = prev ?? loadedContext.definition;
        return applyPatchToDefinition(base, patch);
      });
    },
    [loadedContext]
  );

  const reset = useCallback(() => {
    setPendingDefinition(null);
  }, []);

  const buildSavePayload = useCallback(
    (): SavePayload => ({
      contextUpdate: pendingDefinition ? { definition: pendingDefinition } : null,
    }),
    [pendingDefinition]
  );

  const buildCopyPayload = useCallback(
    (newName: string, newDescription?: string): CopyPayload => {
      if (!loadedContext) {
        throw new Error('Cannot build copy payload: no context loaded');
      }

      const mergedDefinition = pendingDefinition ?? loadedContext.definition;

      const newContext: CreateContextPayload = {
        name: { default: newName },
        description: newDescription ? { default: newDescription } : undefined,
        applicability: loadedContext.applicability,
        definition: mergedDefinition,
        useCaseTags: loadedContext.use_case_tags,
      };

      // The copy carries the loaded view's stored config verbatim: there is no
      // pending view/base config to fold in (see the hook docblock), and the
      // caller's personal overlay is deliberately NOT copied — a copy is a new
      // shared object, and seeding it with one person's arrangement would
      // publish exactly what the overlay exists to keep private.
      const newView: CreateContextViewPayload | null = loadedView
        ? {
            systemName: `${loadedView.system_name}_copy`,
            name: { default: `${newName} View` },
            description: loadedView.description,
            applicability: loadedView.applicability,
            contextRef: newName.toLowerCase().replace(/\s+/g, '_'),
            viewType: loadedView.view_type,
            baseConfig: { ...loadedView.base_config },
            viewConfig: ((loadedView.view_config as Partial<TreeMapViewConfig>) ?? {
              type: 'treemap',
            }) as TreeMapViewConfig,
          }
        : null;

      return { newContext, newView };
    },
    [loadedContext, loadedView, pendingDefinition]
  );

  return {
    isDirty,
    dirtyKinds,
    applyDefinitionPatch,
    reset,
    buildSavePayload,
    buildCopyPayload,
  };
}
